// src/components/InterviewPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import "./InterviewPage.css";
import Header from "./Header";

import ShuHuImg from "../assets/Dr_Shu_Hu.png";
import LiLinImg from "../assets/Li_Lin.png";
import AdityaImg from "../assets/Aditya_Pawar.png";
import ShailImg from "../assets/Shail_Desai.png";

// MUI Accordion
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function InterviewPage() {
  // prevent header overlap on this route only
  useEffect(() => {
    document.body.classList.add("interview-scope");
    return () => document.body.classList.remove("interview-scope");
  }, []);

  /* --------------------------
     People data
  --------------------------- */
  const officers = [
    {
      img: ShuHuImg,
      name: "Dr. Shu Hu",
      role: "Director, Purdue Machine Learning and Media Forensics (M2) Lab",
      qa: [
        {
          q: "What is your vision for AI at Purdue?",
          a: "My vision is to position Purdue as a global leader in responsible AI—linking research, education, and deployment so that our models solve real problems in medicine, national security, agriculture, and media forensics."
        },
        {
          q: "Why DeepFake Detection software?",
          a: "Because the integrity of digital media underpins society’s trust. When anyone can synthesize convincing audio or video, we need defenses that are accurate, fair, and fast. SynthGuard translates our research into a tool that journalists, investigators, and platform teams can actually use."
        },
        {
          q: "Favorite programming language—and why?",
          a: "Python. It lets our team prototype quickly, train at scale, and integrate with the most active ecosystems (PyTorch, TensorFlow, scikit-learn). For Purdue, Python shortens the path from idea → experiment → publication → product."
        },
        {
          q: "How do you keep models fair and trustworthy?",
          a: "We combine spatial, frequency, and demographic-aware strategies, audit performance across sub-groups, and prioritize explainability so stakeholders can interpret results rather than blindly accept them."
        },
        {
          q: "Mentoring philosophy for students?",
          a: "Open notebooks, rigorous baselines, and ablations. Everyone should be able to reproduce a figure and explain why a model works—not just that it does."
        },
        {
          q: "What’s next for the M2 Lab and SynthGuard?",
          a: "Lighter models for edge devices, stronger cross-domain generalization, and collaborative benchmarks with partners so the community can track progress openly."
        }
      ]
    }
  ];

  const members = [
    {
      img: LiLinImg,
      name: "Li Lin",
      role: "Project Manager / Algorithm Design Engineer",
      qa: [
        {
          q: "Why these model families (Naive, Frequency, Fairness-Enhanced, Spatial, Audio)?",
          a: "Each family captures different artifacts. Naive backbones build baselines. Frequency models expose spectral inconsistencies. Fairness-Enhanced methods reduce demographic gaps. Spatial models generalize across methods. Audio addresses voice synthesis separately."
        },
        {
          q: "How much testing went into the release?",
          a: "Cross-dataset and cross-demographic validation, stress tests (compression, scaling, noise), and product-level e2e tests from UI → API → model. Metrics include ROC-AUC, EER, F1, calibration, and failure analysis."
        },
        {
          q: "How do you keep up with evolving gen-AI?",
          a: "Continuous data refresh, synthetic challenge sets, scheduled retraining, and drift monitoring via anonymized (consented) telemetry."
        },
        {
          q: "Most surprising research lesson?",
          a: "Simple augmentations and careful frequency cues often beat heavyweight tricks when generalization matters."
        }
      ]
    },
    {
      img: AdityaImg,
      name: "Aditya Pawar",
      role: "Front-End Engineer",
      qa: [
        {
          q: "How did you get started on the project?",
          a: "I joined to help craft a UI that works for both researchers and non-experts—fast uploads, clear model selection, and interpretable output."
        },
        {
          q: "What did you build?",
          a: "Upload workflow, model tiles, result visualizations (ring confidence displays), and the statistics dashboard."
        },
        {
          q: "Why JS + Python?",
          a: "React for an accessible, responsive UI; Python for the model stack and APIs. That pairing lets us iterate quickly."
        },
        {
          q: "Most useful frontend practices?",
          a: "Small reusable primitives, clean props, predictable state, and accessibility by default."
        }
      ]
    },
    {
      img: ShailImg,
      name: "Shail Desai",
      role: "Full-Stack Engineer / Solutions Architect",
      qa: [
        {
          q: "How is the software architected?",
          a: "A React SPA talks to a FastAPI service that orchestrates inference, auth, and metrics in MySQL. Clean endpoints for uploads/predictions/telemetry; container-friendly."
        },
        {
          q: "Data-driven approach?",
          a: "We collect usage counters and label outcomes (only with consent) to guide retraining and improve robustness."
        },
        {
          q: "UI philosophy?",
          a: "Clarity and speed. We highlight models and results while keeping the visual system consistent and restrained."
        }
      ]
    }
  ];

  /* --------------------------
     LVLM / Roadmap data
  --------------------------- */
  const roadmap = [
    {
      name: "Qwen-VL-Chat",
      type: "LVLM",
      status: "In progress",
      summary:
        "Large Vision-Language Model for multimodal analysis; integrating image OCR, grounding, and VQA for forensic triage.",
      progress: 60
    },
    {
      name: "ALLM4ADD",
      type: "Audio LLM",
      status: "In progress",
      summary:
        "Audio-Language model for DeepFake speech attribution and tamper forensics; diarization + acoustic artifact reasoning.",
      progress: 45
    },
    {
      name: "Next-Gen Spatial & Frequency Models",
      type: "Vision",
      status: "Planned",
      summary:
        "Robust hybrid backbones with frequency priors and patch-level attributions for improved cross-domain generalization.",
      progress: 20
    }
  ];

  /* --------------------------
     UI state
  --------------------------- */
  const [active, setActive] = useState("officers"); // 'officers' | 'members' | 'development' | 'roadmap'
  const [query, setQuery] = useState("");

  const everyone = [...officers, ...members];
  const getCollaborators = (excludeName) => {
    const pool = everyone.filter((p) => p.name !== excludeName);
    const visible = pool.slice(0, 3);
    const remaining = Math.max(pool.length - visible.length, 0);
    return { visible, remaining };
  };

  // filter Q&A by search query (case-insensitive) for whichever panel is active
  const filterQA = (qa) => {
    if (!query.trim()) return qa;
    const q = query.toLowerCase();
    return qa.filter(
      (item) => item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
    );
  };

  const totals = useMemo(() => {
    const qOfficer = officers.reduce((n, o) => n + o.qa.length, 0);
    const qMembers = members.reduce((n, m) => n + m.qa.length, 0);
    return {
      people: everyone.length,
      questions: qOfficer + qMembers,
      lvms: roadmap.length
    };
  }, [everyone.length, officers, members, roadmap.length]);

  return (
    <div className="sgt-wrap">
      <Header />

      {/* Hero */}
      <section className="sgt-hero" aria-labelledby="team-hero-title">
        <h1 id="team-hero-title" className="sgt-title">SynthGuard Team</h1>
        <p className="sgt-subtitle">
          Meet our Director and team. Read concise interviews about the work behind SynthGuard.
        </p>

        {/* Stats bar */}
        <div
          className="sgt-stats"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 12,
            marginTop: 14,
            maxWidth: 720
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--line)",
              borderRadius: 12,
              padding: "10px 14px",
              boxShadow: "var(--shadow-sm)"
            }}
            aria-label="Team size"
          >
            <div style={{ fontSize: 12, color: "var(--muted)" }}>People</div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>{totals.people}</div>
          </div>
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--line)",
              borderRadius: 12,
              padding: "10px 14px",
              boxShadow: "var(--shadow-sm)"
            }}
            aria-label="Interview questions"
          >
            <div style={{ fontSize: 12, color: "var(--muted)" }}>Interview Q&A</div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>{totals.questions}</div>
          </div>
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--line)",
              borderRadius: 12,
              padding: "10px 14px",
              boxShadow: "var(--shadow-sm)"
            }}
            aria-label="Models in roadmap"
          >
            <div style={{ fontSize: 12, color: "var(--muted)" }}>Models (Roadmap)</div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>{totals.lvms}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="sgt-tabs" role="tablist" aria-label="Interview Sections">
          <button
            className={`sgt-tab ${active === "officers" ? "active" : ""}`}
            onClick={() => setActive("officers")}
            role="tab"
            aria-selected={active === "officers"}
          >
            Director
          </button>
          <button
            className={`sgt-tab ${active === "members" ? "active" : ""}`}
            onClick={() => setActive("members")}
            role="tab"
            aria-selected={active === "members"}
          >
            Team Members
          </button>
          <button
            className={`sgt-tab ${active === "development" ? "active" : ""}`}
            onClick={() => setActive("development")}
            role="tab"
            aria-selected={active === "development"}
          >
            How SynthGuard Was Developed
          </button>
          <button
            className={`sgt-tab ${active === "roadmap" ? "active" : ""}`}
            onClick={() => setActive("roadmap")}
            role="tab"
            aria-selected={active === "roadmap"}
          >
            Roadmap (LVLM)
          </button>
        </div>

        {/* Global Q&A search (filters visible accordions) */}
        <div style={{ marginTop: 10, maxWidth: 520 }}>
          <label htmlFor="qa-search" style={{ fontSize: 12, color: "var(--muted)" }}>
            Search Q&A
          </label>
          <input
            id="qa-search"
            type="text"
            placeholder="Search questions or answers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid var(--line)",
              outline: "none",
              background: "#fff",
              boxShadow: "var(--shadow-sm)"
            }}
          />
        </div>
      </section>

      {/* Content */}
      <main className="sgt-main">
        {active === "officers" ? (
          /* ===== Director: compact, professional two-column ===== */
          <section className="sgt-director-section" aria-label="Director Interview">
            <aside className="sgt-director-photo">
              <div className="sgt-director-photo-inner">
                <img src={officers[0].img} alt={officers[0].name} />
              </div>
              <h2 className="sgt-director-name">{officers[0].name}</h2>
              <p className="sgt-director-role">{officers[0].role}</p>
            </aside>

            <div className="sgt-director-qa card">
              <header className="sgt-director-qa-head">
                <h3>Interview</h3>
                <p>Vision, research direction, and the path forward for SynthGuard and the M2 Lab.</p>
              </header>

              <div className="sgt-director-qa-body">
                {filterQA(officers[0].qa).map((qa, i) => (
                  <Accordion key={i} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <span className="sgt-q">{qa.q}</span>
                    </AccordionSummary>
                    <AccordionDetails>
                      <span className="sgt-a">{qa.a}</span>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {filterQA(officers[0].qa).length === 0 && (
                  <div className="sgt-empty">No results for “{query}”.</div>
                )}
              </div>

              <footer className="sgt-director-qa-foot">
                <div className="sgt-avatars stacked">
                  {getCollaborators(officers[0].name).visible.map((c) => (
                    <span className="sgt-avatar" key={c.name} title={c.name}>
                      <img src={c.img} alt={c.name} />
                    </span>
                  ))}
                  {getCollaborators(officers[0].name).remaining > 0 && (
                    <span
                      className="sgt-more"
                      title={`${getCollaborators(officers[0].name).remaining} more`}
                    >
                      +{getCollaborators(officers[0].name).remaining}
                    </span>
                  )}
                  <span className="sgt-label">Collaborators</span>
                </div>
              </footer>
            </div>
          </section>
        ) : active === "members" ? (
          /* ===== Members: tidy grid ===== */
          <div className="sgt-grid">
            {members.map((p) => {
              const collab = getCollaborators(p.name);
              const filtered = filterQA(p.qa);
              return (
                <article key={p.name} className="sgt-card" aria-label={`${p.name} interview`}>
                  <div className="sgt-card-head">
                    <h3 className="sgt-name">{p.name}</h3>
                    <div className="sgt-role">{p.role}</div>
                  </div>

                  <div className="sgt-photo">
                    <img src={p.img} alt={p.name} />
                  </div>

                  <div className="sgt-qa">
                    {filtered.map((qa, i) => (
                      <Accordion key={i} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <span className="sgt-q">{qa.q}</span>
                        </AccordionSummary>
                        <AccordionDetails>
                          <span className="sgt-a">{qa.a}</span>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    {filtered.length === 0 && (
                      <div className="sgt-empty">No results for “{query}”.</div>
                    )}
                  </div>

                  <div className="sgt-avatars stacked">
                    {collab.visible.map((c) => (
                      <span className="sgt-avatar" key={c.name} title={c.name}>
                        <img src={c.img} alt={c.name} />
                      </span>
                    ))}
                    {collab.remaining > 0 && (
                      <span className="sgt-more" title={`${collab.remaining} more`}>
                        +{collab.remaining}
                      </span>
                    )}
                    <span className="sgt-label">Collaborators</span>
                  </div>
                </article>
              );
            })}
          </div>
        ) : active === "development" ? (
          /* ===== Development story (paper) ===== */
          <article className="sgt-paper" aria-label="Development overview">
            <div className="paper-inner">
              <header className="paper-header">
                <h2 className="paper-title">How SynthGuard Was Developed</h2>
                <p className="paper-subtitle">
                  From research prototypes to a production-ready, data-driven platform for studying synthetic media.
                </p>
                <div className="paper-badges" aria-label="Tech stack">
                  <span className="paper-badge">React</span>
                  <span className="paper-badge">FastAPI</span>
                  <span className="paper-badge">Python</span>
                  <span className="paper-badge">MySQL</span>
                  <span className="paper-badge">≈ 2.5 months</span>
                </div>
              </header>

              <div className="paper-grid">
                <aside className="paper-meta">
                  <h4>Authors</h4>
                  <div className="meta-item"><div className="meta-key">Direction</div><div className="meta-val">Dr. Shu Hu</div></div>
                  <div className="meta-item"><div className="meta-key">Algorithms</div><div className="meta-val">Li Lin</div></div>
                  <div className="meta-item"><div className="meta-key">Software / Architecture / UI</div><div className="meta-val">Shail Desai</div></div>
                  <div className="meta-item"><div className="meta-key">Front-End</div><div className="meta-val">Aditya Pawar</div></div>
                  <div className="meta-item"><div className="meta-key">Organization</div><div className="meta-val">Purdue University</div></div>
                </aside>

                <section className="paper-body">
                  <div className="paper-abstract">
                    Dr. Hu and Li Lin had multiple models ready across spatial, frequency, fairness, and audio. To let a wider community try them responsibly, we built a secure, approachable web platform with clear consent controls and a data pipeline for improving robustness.
                  </div>

                  <div className="paper-section">
                    <h3>Motivation</h3>
                    <p>Evaluate diverse detectors under a fairness lens and make them usable for reporters, investigators, and students—no heavy setup required.</p>
                  </div>

                  <div className="paper-callout">
                    “Best-in-class because it couples state-of-the-art models with a data-driven product mindset.”
                  </div>

                  <div className="paper-section">
                    <h3>Architecture</h3>
                    <ul>
                      <li><strong>React SPA</strong> for uploads, model selection, and results.</li>
                      <li><strong>FastAPI</strong> for inference, auth, and telemetry.</li>
                      <li><strong>MySQL</strong> for counters/audit logs (media stored only with consent).</li>
                      <li>Clean separation lets us add/replace detectors quickly.</li>
                    </ul>
                  </div>

                  <div className="paper-section">
                    <h3>Data &amp; Consent</h3>
                    <p>We log usage signals to guide retraining. Media retention is opt-in. This achieves scientific insight without unnecessary collection.</p>
                  </div>

                  <div className="paper-section">
                    <h3>UI/UX</h3>
                    <p>Clarity first: compact cards, consistent spacing, keyboard-friendly interactions, and easy-to-scan Q&amp;A accordions.</p>
                  </div>

                  <div className="paper-section">
                    <h3>Testing &amp; Quality</h3>
                    <ul>
                      <li>Unit tests for preprocessing/utilities</li>
                      <li>E2E coverage: Upload → Predict → Dashboard</li>
                      <li>Cross-dataset & cross-demographic evaluation</li>
                    </ul>
                  </div>

                  <div className="paper-section paper-timeline">
                    <h3>Timeline (≈ 2.5 months)</h3>
                    <div className="milestone"><h5>Week 1–2</h5><p>Scaffolding, auth, upload pipeline</p></div>
                    <div className="milestone"><h5>Week 3–6</h5><p>Model wiring, dashboard counters, QA passes</p></div>
                    <div className="milestone"><h5>Week 7–10</h5><p>Refinements, accessibility, docs</p></div>
                  </div>

                  <div className="paper-footer">
                    A detailed paper is in preparation, covering datasets, evaluation, and design trade-offs.
                  </div>
                </section>
              </div>
            </div>
          </article>
        ) : (
          /* ===== Roadmap (LVLM) ===== */
          <article className="sgt-paper" aria-label="Model roadmap">
            <div className="paper-inner">
              <header className="paper-header">
                <h2 className="paper-title">Model Roadmap</h2>
                <p className="paper-subtitle">
                  LVLM development in progress — <strong>Qwen-VL-Chat</strong>, <strong>ALLM4ADD (Audio LLM)</strong>, with additional models to follow.
                </p>
              </header>

              <section
                className="paper-body"
                style={{ gridTemplateColumns: "1fr", display: "grid", gap: 16 }}
              >
                {roadmap.map((item) => (
                  <div
                    key={item.name}
                    className="paper-section"
                    style={{ display: "grid", gap: 8 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                        <h3 style={{ margin: 0 }}>{item.name}</h3>
                        <span
                          className="paper-badge"
                          aria-label={`Model type: ${item.type}`}
                          style={{ fontWeight: 700 }}
                        >
                          {item.type}
                        </span>
                      </div>
                      <span
                        className="paper-badge"
                        aria-label={`Status: ${item.status}`}
                        style={{
                          background:
                            item.status === "In progress" ? "#eef5ff" : "#f5f7fa"
                        }}
                      >
                        {item.status}
                      </span>
                    </div>

                    <p style={{ margin: 0 }}>{item.summary}</p>

                    {/* Progress meter */}
                    <div
                      aria-label="progress"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={item.progress}
                      style={{
                        marginTop: 6,
                        width: "100%",
                        height: 10,
                        borderRadius: 999,
                        background: "var(--line-2, #eef1f6)",
                        overflow: "hidden",
                        border: "1px solid var(--line)"
                      }}
                    >
                      <div
                        style={{
                          width: `${item.progress}%`,
                          height: "100%",
                          background:
                            "linear-gradient(90deg, var(--accent), var(--accent-2))"
                        }}
                      />
                    </div>
                  </div>
                ))}

                {/* Follow-on models note */}
                <div className="paper-section" style={{ background: "#f8fbff" }}>
                  <strong>Other models to follow:</strong> expanded LVLMs for
                  multi-frame video reasoning, cross-lingual audio forensics,
                  and patch-level attributions for image tampering.
                </div>
              </section>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
